import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type MiembroPublico = {
  id: string;
  user_id: string;
  slug: string;
  nombre: string;
  apellidos: string | null;
  email: string | null;
  telefono: string | null;
  bio: string | null;
  foto_url: string | null;
  cargo: string | null;
  agencia: { id: string; slug: string | null; nombre: string; logo_url: string | null } | null;
  anuncios_asignados_count: number;
};

/**
 * Ficha pública de un agente: solo contactos con slug cuyo usuario es miembro
 * real (agencia_miembros) de una agencia no bloqueada.
 */
export const getMiembroPublico = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) =>
    z.object({ slug: z.string().trim().min(1).max(120).regex(/^[a-z0-9-]+$/) }).parse(d),
  )
  .handler(async ({ data }): Promise<MiembroPublico | null> => {
    const { supabaseAdmin: sb } = await import("@/integrations/supabase/client.server");
    const { data: c, error } = await sb
      .from("contactos")
      .select(
        "id, user_id, slug, nombre, apellidos, email, telefono, bio, foto_url, anuncios_asignados_count, cargos(nombre)",
      )
      .eq("slug", data.slug)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!c || !c.user_id) return null;

    const { data: mem } = await sb
      .from("agencia_miembros")
      .select("agencia_id, created_at, agencias(id, slug, nombre, logo_url, estado)")
      .eq("user_id", c.user_id)
      .order("created_at", { ascending: true });
    const activo = (mem ?? []).find((m: any) => m.agencias && m.agencias.estado !== "bloqueada") as any;
    if (!activo) return null;

    return {
      id: c.id,
      user_id: c.user_id,
      slug: c.slug as string,
      nombre: c.nombre,
      apellidos: c.apellidos ?? null,
      email: c.email ?? null,
      telefono: c.telefono ?? null,
      bio: c.bio ?? null,
      foto_url: c.foto_url ?? null,
      cargo: (c as any).cargos?.nombre ?? null,
      agencia: {
        id: activo.agencias.id,
        slug: activo.agencias.slug ?? null,
        nombre: activo.agencias.nombre,
        logo_url: activo.agencias.logo_url ?? null,
      },
      anuncios_asignados_count: Number(c.anuncios_asignados_count ?? 0),
    };
  });

/** Equipo público de una agencia: miembros con slug. */
export const listMiembrosPublicosAgencia = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ agencia_id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin: sb } = await import("@/integrations/supabase/client.server");
    const { data: mems } = await sb
      .from("agencia_miembros")
      .select("user_id")
      .eq("agencia_id", data.agencia_id);
    const ids = (mems ?? []).map((m: any) => m.user_id).filter(Boolean);
    if (!ids.length) return [] as Array<{ slug: string; nombre: string; apellidos: string | null; foto_url: string | null; cargo: string | null }>;
    const { data: cs } = await sb
      .from("contactos")
      .select("slug, nombre, apellidos, foto_url, cargos(nombre)")
      .in("user_id", ids)
      .not("slug", "is", null)
      .order("nombre");
    return (cs ?? []).map((c: any) => ({
      slug: c.slug as string,
      nombre: c.nombre as string,
      apellidos: (c.apellidos ?? null) as string | null,
      foto_url: (c.foto_url ?? null) as string | null,
      cargo: (c.cargos?.nombre ?? null) as string | null,
    }));
  });
